import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { maintenanceService } from '../../lib/maintenanceService';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AssignedWorkOrders from './components/AssignedWorkOrders';
import EquipmentScanner from './components/EquipmentScanner';
import WorkOrderDetails from './components/WorkOrderDetails';
import PartsInventoryLookup from './components/PartsInventoryLookup';
import TimeTracker from './components/TimeTracker';
import MaintenanceAlerts from './components/MaintenanceAlerts';

const MobileMaintenanceOperations = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('work-orders');
  const [assignments, setAssignments] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator?.onLine);
  const [syncQueue, setSyncQueue] = useState([]);

  useEffect(() => {
    if (userProfile?.id) {
      loadTechnicianData();
    }
  }, [userProfile]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncOfflineData();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncQueue]);

  const loadTechnicianData = async () => {
    setLoading(true);
    try {
      const [assignmentsRes, alertsRes] = await Promise.all([
        maintenanceService?.getTechnicianAssignments(userProfile?.id, { status: 'assigned,accepted,in_progress' }),
        maintenanceService?.getMaintenanceAlerts(userProfile?.gym_id, true)
      ]);

      if (!assignmentsRes?.error) {
        setAssignments(assignmentsRes?.data || []);
      }

      if (!alertsRes?.error) {
        setAlerts(alertsRes?.data || []);
      }
    } catch (error) {
      console.error('Error loading technician data:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncOfflineData = async () => {
    if (syncQueue?.length === 0) return;

    console.log('Syncing offline data...', syncQueue?.length, 'items');
    // Process sync queue here
    setSyncQueue([]);
  };

  const handleAcceptWorkOrder = async (assignmentId) => {
    try {
      const { error } = await maintenanceService?.acceptWorkOrder(assignmentId);
      if (!error) {
        loadTechnicianData();
      }
    } catch (error) {
      console.error('Error accepting work order:', error);
    }
  };

  const handleStartWorkOrder = async (assignmentId) => {
    try {
      if (navigator?.geolocation) {
        navigator?.geolocation?.getCurrentPosition(async (position) => {
          const location = {
            latitude: position?.coords?.latitude,
            longitude: position?.coords?.longitude
          };
          const { error } = await maintenanceService?.startWorkOrder(assignmentId, location);
          if (!error) {
            loadTechnicianData();
          }
        });
      } else {
        const { error } = await maintenanceService?.startWorkOrder(assignmentId);
        if (!error) {
          loadTechnicianData();
        }
      }
    } catch (error) {
      console.error('Error starting work order:', error);
    }
  };

  const handleCompleteWorkOrder = async (assignmentId) => {
    try {
      const { error } = await maintenanceService?.completeWorkOrder(assignmentId);
      if (!error) {
        loadTechnicianData();
        setSelectedWorkOrder(null);
      }
    } catch (error) {
      console.error('Error completing work order:', error);
    }
  };

  const handleEquipmentScanned = async (equipmentData) => {
    console.log('Equipment scanned:', equipmentData);
    // Load equipment details and maintenance history
  };

  const tabs = [
    { id: 'work-orders', label: 'Work Orders', icon: 'ClipboardList', count: assignments?.length },
    { id: 'scanner', label: 'Scanner', icon: 'ScanLine' },
    { id: 'parts', label: 'Parts', icon: 'Package' },
    { id: 'alerts', label: 'Alerts', icon: 'Bell', count: alerts?.length },
    { id: 'time', label: 'Time', icon: 'Clock' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading maintenance operations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="bg-card border-b border-border sticky top-0 z-10">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Mobile Maintenance</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {userProfile?.full_name} • Field Operations
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!isOnline && (
                <div className="flex items-center gap-1 px-3 py-1.5 bg-warning/10 rounded-lg">
                  <Icon name="WifiOff" size={16} className="text-warning" />
                  <span className="text-xs font-medium text-warning">Offline</span>
                </div>
              )}
              {isOnline && syncQueue?.length > 0 && (
                <div className="flex items-center gap-1 px-3 py-1.5 bg-primary/10 rounded-lg">
                  <Icon name="RefreshCw" size={16} className="text-primary animate-spin" />
                  <span className="text-xs font-medium text-primary">Syncing...</span>
                </div>
              )}
              <Button variant="outline" size="sm" iconName="RefreshCw" onClick={loadTechnicianData} />
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center gap-2 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activeTab === tab?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                <span className="text-sm font-medium">{tab?.label}</span>
                {tab?.count > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    activeTab === tab?.id ? 'bg-primary-foreground/20' : 'bg-primary/20 text-primary'
                  }`}>
                    {tab?.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {activeTab === 'work-orders' && (
          <AssignedWorkOrders
            assignments={assignments}
            onAccept={handleAcceptWorkOrder}
            onStart={handleStartWorkOrder}
            onComplete={handleCompleteWorkOrder}
            onSelectWorkOrder={setSelectedWorkOrder}
          />
        )}

        {activeTab === 'scanner' && (
          <EquipmentScanner onEquipmentScanned={handleEquipmentScanned} />
        )}

        {activeTab === 'parts' && (
          <PartsInventoryLookup gymId={userProfile?.gym_id} />
        )}

        {activeTab === 'alerts' && (
          <MaintenanceAlerts alerts={alerts} onRefresh={loadTechnicianData} />
        )}

        {activeTab === 'time' && (
          <TimeTracker technicianId={userProfile?.id} />
        )}
      </div>

      {/* Work Order Details Modal */}
      {selectedWorkOrder && (
        <WorkOrderDetails
          workOrder={selectedWorkOrder}
          onClose={() => setSelectedWorkOrder(null)}
          onComplete={() => handleCompleteWorkOrder(selectedWorkOrder?.id)}
        />
      )}
    </div>
  );
};

export default MobileMaintenanceOperations;